import { useState } from 'react'
import { Trash2, Plus, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { SearchableSelect } from './SearchableSelect'
import { WorkspaceApps } from './WorkspaceApps'
import { useClients } from '../hooks/useClients'
import { useAllowedDomains } from '../hooks/useAllowedDomains'
import type { Workspace, WorkspaceMember, SeedAppConfig } from '../types'

interface WorkspaceEditorProps {
  workspaces: Workspace[]
  onChange: (next: Workspace[]) => void
  availableApps: string[]
  /** Manifest app config from the served version — supplies allow-list labels. */
  appsConfig?: Record<string, SeedAppConfig>
  /** The restricted apps of the version the share serves (BARN-345); absent = none. */
  restrictedApps?: Record<string, SeedAppConfig>
  /** Stripped from label prefixes, exactly as the container's app bar does. */
  seedDisplayName?: string
  /** Disable structural changes (add/remove workspaces) — used when editing a single existing one. */
  allowAddRemove?: boolean
}

/**
 * Edit the list of workspaces on a share. Each workspace card holds:
 *   - editable name (required, unique)
 *   - member chips (emails + domains) with add/remove
 *   - per-workspace apps allow-list
 */
export function WorkspaceEditor({
  workspaces,
  onChange,
  availableApps,
  appsConfig,
  restrictedApps,
  seedDisplayName,
  allowAddRemove = true,
}: WorkspaceEditorProps) {
  function patch(idx: number, next: Partial<Workspace>) {
    const copy = [...workspaces]
    copy[idx] = { ...copy[idx], ...next }
    onChange(copy)
  }

  function addWorkspace() {
    onChange([
      ...workspaces,
      { name: nextDefaultName(workspaces), members: [], apps: null, resetEnabled: false },
    ])
  }

  function removeWorkspace(idx: number) {
    if (workspaces.length === 1) return
    onChange(workspaces.filter((_, i) => i !== idx))
  }

  function setMembers(idx: number, members: WorkspaceMember[]) {
    patch(idx, { members })
  }

  return (
    <div className="space-y-3">
      {workspaces.map((w, idx) => (
        <Card key={w.id ?? `new-${idx}`}>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <Input
                value={w.name}
                onChange={e => patch(idx, { name: e.target.value })}
                placeholder="Workspace name (e.g. Mars, Internal)"
                className="font-medium"
              />
              {allowAddRemove && workspaces.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeWorkspace(idx)}
                  className="text-destructive"
                  title="Remove workspace (cascade deletes its discussion)"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <MemberList
              members={w.members}
              onChange={members => setMembers(idx, members)}
            />

            <div className="border-t pt-2">
              <div className="text-xs text-muted-foreground mb-1">Apps allow-list</div>
              <WorkspaceApps
                apps={w.apps}
                allApps={availableApps}
                appsConfig={appsConfig}
                restrictedApps={restrictedApps}
                seedDisplayName={seedDisplayName}
                onChange={apps => patch(idx, { apps })}
              />
            </div>

            <div className="border-t pt-2">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={w.resetEnabled ?? false}
                  onChange={e => patch(idx, { resetEnabled: e.target.checked })}
                  className="mt-0.5"
                />
                <span>
                  Allow data reset
                  <span className="block text-xs text-muted-foreground">
                    Shows a reset button in the container for this workspace's members. Resets the
                    share's data for this version to its original state — affects everyone viewing
                    this share at this version.
                  </span>
                </span>
              </label>
            </div>
          </CardContent>
        </Card>
      ))}

      {allowAddRemove && (
        <Button type="button" variant="outline" size="sm" onClick={addWorkspace}>
          <Plus className="h-4 w-4 mr-1" /> Add workspace
        </Button>
      )}
    </div>
  )
}

function nextDefaultName(existing: Workspace[]): string {
  const base = 'Workspace'
  const taken = new Set(existing.map(w => w.name.trim().toLowerCase()))
  if (!taken.has(base.toLowerCase())) return base
  for (let i = 2; i < 999; i++) {
    const candidate = `${base} ${i}`
    if (!taken.has(candidate.toLowerCase())) return candidate
  }
  return `${base} ${Date.now()}`
}

function MemberList({
  members,
  onChange,
}: {
  members: WorkspaceMember[]
  onChange: (next: WorkspaceMember[]) => void
}) {
  const { clients } = useClients()
  const { domains } = useAllowedDomains()
  const [kind, setKind] = useState<'email' | 'domain'>('email')
  const [pendingValue, setPendingValue] = useState('')

  const seen = new Set(members.map(m => m.recipient))
  const clientOptions = (clients || [])
    .filter(c => c.role !== 'admin' && !seen.has(c.email))
    .map(c => ({ value: c.email, label: c.name ? `${c.name} <${c.email}>` : c.email }))
  const domainOptions = (domains || [])
    .filter(d => !seen.has(d.domain))
    .map(d => ({ value: d.domain, label: d.domain }))

  function add() {
    const recipient = pendingValue.trim().toLowerCase()
    if (!recipient || seen.has(recipient)) return
    onChange([...members, { recipient, kind }])
    setPendingValue('')
  }

  function remove(recipient: string) {
    onChange(members.filter(m => m.recipient !== recipient))
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {members.length === 0 ? (
          <span className="text-xs text-muted-foreground italic">No members yet</span>
        ) : (
          members.map(m => (
            <Badge key={m.recipient} variant="secondary" className="gap-1.5">
              <span className="text-xs">{m.kind === 'domain' ? `@${m.recipient}` : m.recipient}</span>
              <button
                type="button"
                onClick={() => remove(m.recipient)}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
      <div className="flex gap-1.5 items-center">
        <div className="inline-flex rounded-md border overflow-hidden">
          <button
            type="button"
            onClick={() => { setKind('email'); setPendingValue('') }}
            className={`px-2 py-1 text-xs ${kind === 'email' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
          >Email</button>
          <button
            type="button"
            onClick={() => { setKind('domain'); setPendingValue('') }}
            className={`px-2 py-1 text-xs ${kind === 'domain' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
          >Domain</button>
        </div>
        <div className="flex-1">
          <SearchableSelect
            options={kind === 'email' ? clientOptions : domainOptions}
            value={pendingValue}
            onChange={setPendingValue}
            placeholder={kind === 'email' ? 'Pick a client…' : 'Pick a domain…'}
          />
        </div>
        <Button type="button" size="sm" onClick={add} disabled={!pendingValue}>
          Add
        </Button>
      </div>
    </div>
  )
}
